import { useRef, useState } from "react";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import FolderIcon from "@mui/icons-material/Folder";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import {
  type DocumentKind,
  type PractitionerDocument,
  deleteMyDocument,
  downloadMyDocument,
  listMyDocuments,
  uploadMyDocument,
} from "@/api/documents";

const KINDS: DocumentKind[] = [
  "DIPLOMA",
  "RPPS_ATTEST",
  "DPC_CERT",
  "LIABILITY_INSURANCE",
  "MEDICAL_DEGREE",
  "ID_CARD",
  "OTHER",
];

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function expiryChip(doc: PractitionerDocument, t: TFunction): JSX.Element | null {
  if (doc.is_expired) {
    return <Chip size="small" color="error" label={t("documents.expired")} />;
  }
  if (doc.days_until_expiry !== null && doc.days_until_expiry <= 60) {
    return (
      <Chip
        size="small"
        color="warning"
        label={t("documents.expiringSoon", { days: doc.days_until_expiry })}
      />
    );
  }
  if (doc.expires_at) {
    return <Chip size="small" variant="outlined" label={doc.expires_at} />;
  }
  return null;
}

export function DocumentsPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const docs = useQuery({
    queryKey: ["my-documents"],
    queryFn: listMyDocuments,
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<DocumentKind>("DIPLOMA");
  const [label, setLabel] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const resetForm = () => {
    setFile(null);
    setKind("DIPLOMA");
    setLabel("");
    setIssuedAt("");
    setExpiresAt("");
    setServerError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("no_file");
      return uploadMyDocument({
        file,
        kind,
        label: label.trim() || file.name,
        issued_at: issuedAt || undefined,
        expires_at: expiresAt || undefined,
      });
    },
    onSuccess: () => {
      setUploadOpen(false);
      resetForm();
      qc.invalidateQueries({ queryKey: ["my-documents"] });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response
        ?.data?.detail;
      setServerError(detail || "error");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMyDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-documents"] }),
  });

  const download = async (doc: PractitionerDocument) => {
    const { blob, filename } = await downloadMyDocument(doc.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const rows = docs.data ?? [];

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              <FolderIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("documents.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("documents.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            {t("documents.upload")}
          </Button>
        </Stack>

        {docs.isLoading ? (
          <CircularProgress size={20} />
        ) : rows.length === 0 ? (
          <Alert severity="info">{t("documents.empty")}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("documents.col.label")}</TableCell>
                <TableCell>{t("documents.col.kind")}</TableCell>
                <TableCell>{t("documents.col.size")}</TableCell>
                <TableCell>{t("documents.col.expiry")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {d.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {d.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={t(`documents.kind.${d.kind}`)} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {fmtSize(d.size_bytes)}
                  </TableCell>
                  <TableCell>{expiryChip(d, t)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("documents.download")}>
                      <IconButton size="small" onClick={() => download(d)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("documents.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(t("documents.deleteConfirm")))
                            deleteMutation.mutate(d.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          resetForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("documents.uploadTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {serverError && <Alert severity="error">{t(`documents.err.${serverError}`, { defaultValue: serverError })}</Alert>}
            <Box>
              <input
                ref={inputRef}
                type="file"
                hidden
                aria-label="Select document file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  if (f && !label) setLabel(f.name.replace(/\.[^.]+$/, ""));
                }}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => inputRef.current?.click()}
              >
                {t("documents.pickFile")}
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {file.name} — {fmtSize(file.size)}
                </Typography>
              )}
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>{t("documents.kindLabel")}</InputLabel>
              <Select
                label={t("documents.kindLabel")}
                value={kind}
                onChange={(e) => setKind(e.target.value as DocumentKind)}
              >
                {KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {t(`documents.kind.${k}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={t("documents.labelField")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                label={t("documents.issuedAt")}
                type="date"
                value={issuedAt}
                onChange={(e) => setIssuedAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label={t("documents.expiresAt")}
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("documents.acceptedHelp")}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadOpen(false);
              resetForm();
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => uploadMutation.mutate()}
            disabled={!file || !label.trim() || uploadMutation.isPending}
          >
            {t("documents.upload")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
